/**
 * Admin/master only: every open proposal as current → proposed, with an
 * optional reason and อนุมัติ / ปฏิเสธ. A decision someone else already made
 * says so and offers a reload; nothing is overwritten silently.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { planFor, resolveCprTarget, type CprTargetRow, type PlanRow } from '@/services/targets/targetRules';
import type { ResultMetric } from '@/services/targets/resultMetric';
import { nextMonthStart } from '@/services/dataDate';
import { decideProposal } from '@/services/targets/targetsService';
import { announceTargetsChanged } from '@/services/targets/targetsEvents';
import { failureOf, keyPlatformLabel, COST_LABEL, money, OBJECTIVE_LABEL, whoLabel, type WriteFailure } from './targetUi';

interface Props {
  plans: readonly PlanRow[];
  targets: readonly CprTargetRow[];
  me: string | null;
  today: string;
  reload: () => void;
}

interface Item {
  kind: 'plan' | 'target';
  id: string;
  /** DEC3: the metric a target proposal was made under (pending proposals of different metrics can coexist on one key); null for plans. */
  metric: ResultMetric | null;
  label: string;
  current: number | null;
  proposed: number;
  currency: string;
  proposedBy: string;
  /** False when the server would refuse approval (closed month, passed start date): reject only. */
  approvable: boolean;
}

const planItem = (t: TFunction, plans: readonly PlanRow[], today: string, r: PlanRow): Item => ({
  kind: 'plan', id: r.id, metric: null, currency: r.currency, proposedBy: r.proposedBy, proposed: r.amount,
  label: `${t('targets.kind.plan')} ${r.month.slice(0, 7)} · ${keyPlatformLabel(t, r.platform)}`,
  current: planFor(plans, r.month, r.currency, r.platform)?.amount ?? null,
  approvable: nextMonthStart(r.month) > today,
});

const targetItem = (t: TFunction, targets: readonly CprTargetRow[], today: string, r: CprTargetRow): Item => ({
  kind: 'target', id: r.id, metric: r.metric, currency: r.currency, proposedBy: r.proposedBy, proposed: r.target,
  label: `${OBJECTIVE_LABEL[r.objective](t)} (${COST_LABEL[r.metric](t)}) · ${keyPlatformLabel(t, r.platform)} · ${t('targets.cpr.effectiveFrom')} ${r.effectiveFrom}`,
  current: resolveCprTarget(targets, { objective: r.objective, metric: r.metric, currency: r.currency, platform: r.platform, date: r.effectiveFrom })?.target ?? null,
  approvable: r.effectiveFrom >= today,
});

const ProposalRow: React.FC<{ item: Item; me: string | null; reload: () => void }> = ({ item, me, reload }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [failure, setFailure] = useState<WriteFailure | null>(null);
  const [busy, setBusy] = useState(false);

  const decide = async (approve: boolean) => {
    setBusy(true);
    setFailure(null);
    try {
      await decideProposal(item.kind, item.id, approve, reason.trim() || undefined);
      announceTargetsChanged();
      reload();
    } catch (err: unknown) {
      setFailure(failureOf(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-3 border-t border-neutral-100 text-sm">
      <p className="font-medium text-ink-900">{item.label}</p>
      <p className="text-ink-900">
        {item.current === null ? t('targets.none') : money(item.current, item.currency)} → {money(item.proposed, item.currency)}
      </p>
      <p className="text-xs text-neutral-700">{t('targets.history.proposedBy', { who: whoLabel(t, item.proposedBy, me) })}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input type="text" aria-label={t('targets.reasonOptionalAria', { key: item.label })} placeholder={t('targets.reasonOptional')}
          className="rounded border border-neutral-300 px-2 py-1 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} />
        {item.approvable && (
          <button type="button" disabled={busy} className="rounded px-3 py-1 text-xs font-medium bg-ink-950 text-white"
            onClick={() => void decide(true)}>{t('targets.approve')}</button>
        )}
        <button type="button" disabled={busy} className="rounded px-3 py-1 text-xs font-medium border border-neutral-300"
          onClick={() => void decide(false)}>{t('targets.reject')}</button>
      </div>
      {!item.approvable && <p className="mt-1 text-xs text-warning-700">{t('targets.cannotApprove')}</p>}
      {failure && (
        <div className="mt-1">
          <p role="alert" className="text-xs text-error-700">{failure.message}</p>
          {failure.offerReload && (
            <button type="button" className="text-xs underline" onClick={reload}>{t('targets.reload')}</button>
          )}
        </div>
      )}
    </li>
  );
};

export const PendingProposals: React.FC<Props> = ({ plans, targets, me, today, reload }) => {
  const { t } = useTranslation();
  const items = [
    ...plans.filter((r) => r.status === 'proposed').map((r) => planItem(t, plans, today, r)),
    ...targets.filter((r) => r.status === 'proposed').map((r) => targetItem(t, targets, today, r)),
  ];
  if (items.length === 0) return null;
  return (
    <section className="bg-warning-50 rounded-xl border border-warning-200 p-6">
      <h2 className="text-sm font-semibold text-ink-900">{t('targets.pendingBanner', { count: items.length })}</h2>
      <ul className="mt-2">
        {items.map((item) => <ProposalRow key={`${item.kind}-${item.id}`} item={item} me={me} reload={reload} />)}
      </ul>
    </section>
  );
};
